// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import ExternalPageLinkButton from 'presentations/views/components/molecules/ExternalPageLinkButton';
import Link from 'presentations/views/components/atoms/Link';
import styled from '@emotion/styled';
import { gray04, primary, white } from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
  fontWeight,
  mediaMin,
} from 'presentations/views/styles';
import Progress from 'presentations/views/components/atoms/Progress';
import Icon from 'presentations/views/components/atoms/Icon';
import { ViewerItem } from 'presentations/api/schema/user';
import { useServicePaymentPage } from './hooks';
import Image from 'presentations/views/components/atoms/Image';
import { useContext } from 'react';
import DashboardServiceNavigation, {
  SectionAdminView,
} from 'presentations/views/components/organisms/DashboardServiceNavigation';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import {
  pricingPath,
  stripeConnectDashboardPath,
  stripeFeePath,
  stripePath,
} from 'presentations/routes/path';
import { ShopAccountStatus } from 'domains/models/team_shop_account/status';
import { Role } from 'domains/models/team_user/role';
import PaymentFeature from 'domains/models/subscription/features/payment';
import FormPaidIcon from 'presentations/views/components/molecules/FormPaidIcon';
import { PlanId } from 'domains/models/subscription/planId';
import { useRouter } from 'next/router';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useServicePaymentPage({
    viewer,
  });

  const isError = viewer.role > Role.Admin;
  const paymentFeature = new PaymentFeature({
    planVersion: viewer.planVersion,
  });
  const isEnable = paymentFeature.isValid(viewer.planId);
  const router = useRouter();
  const feePath = stripeFeePath(router.locale);

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      isVisibleSupport
      customHeader={
        <DashboardServiceNavigation
          viewer={viewer}
          activeIndex={SectionAdminView.payment}
        />
      }
      tabBarContent={
        state.isLoading ? (
          <ProgressContainer>
            <Progress />
          </ProgressContainer>
        ) : (
          <Container>
            {!isError && (
              <Main>
                <HostServicePaymentContainer>
                  <ActionContainer>
                    <AvatarContainer>
                      <ProfileName>{t('Payment')}</ProfileName>
                    </AvatarContainer>
                  </ActionContainer>
                  <StartContainer>
                    <Box>
                      {state.shopAccount?.status ===
                      ShopAccountStatus.Enable ? (
                        <>
                          <StartDesc>{t('Dashboard Desc')}</StartDesc>
                          <ExternalPageLinkButton
                            label={t('dashboard')}
                            url={stripeConnectDashboardPath(
                              state.shopAccount.stripeAccountId,
                            )}
                            image={
                              <Logo
                                alt="stripe"
                                src="integrations/stripe.svg"
                              />
                            }
                          />
                          <StartButtonDesc>
                            {t('Start button desc.0')}
                            <Link href={feePath} target="_blank">
                              {t('Start button desc.1')}
                              <Icon
                                name="checkbox-multiple-blank-outline"
                                size={fontSize(14)}
                              />
                            </Link>
                          </StartButtonDesc>
                          <DashboardButtonDesc>
                            {t('Dashboard button Desc')}
                          </DashboardButtonDesc>
                        </>
                      ) : (
                        <>
                          <StartTitle>
                            {t('Start title')}
                            {!isEnable && (
                              <PaidLink href={pricingPath()}>
                                <FormPaidIcon plan={PlanId.Team} />
                              </PaidLink>
                            )}
                          </StartTitle>
                          <StartDesc>{t('Start desc')}</StartDesc>
                          <ExternalPageLinkButton
                            label={t('connect')}
                            onClick={actions.onConnect}
                            isDisabled={!isEnable}
                            image={
                              <Logo
                                alt="stripe"
                                src="integrations/stripe.svg"
                              />
                            }
                          />
                          <StartButtonDesc>
                            {t('Start button desc.0')}
                            <Link href={feePath} target="_blank">
                              {t('Start button desc.1')}
                              <Icon
                                name="checkbox-multiple-blank-outline"
                                size={fontSize(14)}
                              />
                            </Link>
                          </StartButtonDesc>
                          <SubTitle>{t('Sub title')}</SubTitle>
                          <StartDesc>
                            {t('Sub desc.0')}
                            <Link href={stripePath()} target="_blank">
                              {t('Sub desc.1')}
                            </Link>
                            {t('Sub desc.2')}
                          </StartDesc>
                          <ExampleTitle>{t('Example title')}</ExampleTitle>
                          <ExampleContainer>
                            <ExampleItem>
                              <ExampleItemName>
                                {t('Example Item 1')}
                              </ExampleItemName>
                            </ExampleItem>
                            <ExampleSubItemContainer>
                              <ExampleSubItem>
                                <ExampleSubItemName>
                                  {t('Example Item 2')}
                                </ExampleSubItemName>
                                <ExampleSubItemValue>
                                  {t('Example Item 2 amount')}
                                </ExampleSubItemValue>
                              </ExampleSubItem>
                              <ExampleSubItem>
                                <ExampleSubItemName>
                                  {t('Example Item 4')}
                                </ExampleSubItemName>
                                <ExampleSubItemValue>
                                  {t('Example Item 4 amount')}
                                </ExampleSubItemValue>
                              </ExampleSubItem>
                            </ExampleSubItemContainer>
                            <ExampleItem>
                              <ExampleItemName>
                                {t('Example Item 5')}
                              </ExampleItemName>
                              <ExampleItemValue>
                                {t('Example Item 5 amount')}
                              </ExampleItemValue>
                            </ExampleItem>
                          </ExampleContainer>
                          <ExampleDesc>{t('Example desc')}</ExampleDesc>
                          <BottomButtonContainer>
                            <BottomButtonLabel>
                              {t('Create Stripe Account Button Label')}
                            </BottomButtonLabel>
                            <BottomButtonDesc>
                              {t('Create Stripe Account Button Desc')}
                            </BottomButtonDesc>
                            <BottomButtonOuter>
                              <ExternalPageLinkButton
                                label={t('connect')}
                                onClick={actions.onConnect}
                                isDisabled={!isEnable}
                                image={
                                  <Logo
                                    alt="stripe"
                                    src="integrations/stripe.svg"
                                  />
                                }
                              />
                            </BottomButtonOuter>
                          </BottomButtonContainer>
                        </>
                      )}
                    </Box>
                  </StartContainer>
                </HostServicePaymentContainer>
              </Main>
            )}
          </Container>
        )
      }
    />
  );
}

const Container = styled.div`
  display: flex;
  ${mediaMax.tablet} {
    flex-direction: column;
  }
  padding-bottom: ${spacing(24)};
`;
const Main = styled.div`
  flex: 1;
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: 0;
  }
`;

const HostServicePaymentContainer = styled.div`
  margin-top: ${spacing(48)};
  padding-left: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    padding: 0 ${spacing(12)};
  }
`;
const ProfileName = styled.div`
  font-size: ${fontSize(24)};
  font-weight: bold;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;

const ActionContainer = styled.div`
  margin: 0 auto ${spacing(24)};
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    margin: 0 auto ${spacing(16)};
    align-items: center;
    /* display: block;
    width: 100%; */
  }
  text-align: right;
`;

const StartContainer = styled.div`
  min-height: 100%;
  padding: ${spacing(32)};
  background-color: ${white};
  border-radius: ${borderRadius(16)};

  ${mediaMax.tablet} {
    padding: ${spacing(20)} ${spacing(12)};
    margin: ${spacing(16)} ${spacing(12)};
  }
`;
const Box = styled.div`
  flex-basis: 50%;
  min-width: 50%;
`;

const StartTitle = styled.div`
  font-size: ${spacing(24)};
  font-weight: bold;
  margin-bottom: ${spacing(16)};
`;
const StartDesc = styled.div`
  font-size: ${spacing(14)};
  margin-bottom: ${spacing(16)};
  white-space: pre-wrap;
`;
const StartButtonDesc = styled.div`
  font-size: ${spacing(12)};
  margin-top: ${spacing(16)};
`;
const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    /* margin-bottom: ${spacing(24)}; */
  }
`;
const Logo = styled(Image)`
  margin-right: ${spacing(20)};
  width: 72px;
`;
const SubTitle = styled.div`
  font-size: ${spacing(20)};
  font-weight: bold;
  margin: ${spacing(16)} 0;
`;
const ExampleTitle = styled.div`
  font-size: ${spacing(16)};
  font-weight: bold;
  margin-bottom: ${spacing(16)};
`;
const ExampleContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${gray04};
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: ${spacing(16)};

  ${mediaMin.tablet} {
    width: 50%;
  }
`;
const ExampleItem = styled.div`
  background-color: ${white};
  padding: ${spacing(24)};
`;
const ExampleItemName = styled.span`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
`;
const ExampleItemValue = styled.span`
  float: right;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
`;
const ExampleSubItemContainer = styled.div`
  border-top: 1px solid ${gray04};
  border-bottom: 1px solid ${gray04};
  margin: 0 ${spacing(24)};
`;
const ExampleSubItem = styled.div`
  font-size: ${fontSize(14)};
  margin: ${spacing(24)} 0;
`;
const ExampleSubItemName = styled.span`
  font-size: ${fontSize(14)};
`;
const ExampleSubItemValue = styled.span`
  float: right;
  font-size: ${fontSize(14)};
`;
const ExampleDesc = styled.div`
  font-size: ${spacing(14)};
  margin-bottom: ${spacing(16)};
`;
const BottomButtonContainer = styled.div`
  border: 1px solid ${primary};
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: ${spacing(16)};
`;
const BottomButtonLabel = styled.div`
  font-size: ${spacing(16)};
  color: ${primary};
  padding: 0 ${spacing(24)};
  margin-top: ${spacing(24)};
  font-weight: bold;
`;
const BottomButtonDesc = styled.div`
  padding: 0 ${spacing(24)};
  margin-top: ${spacing(16)};
`;
const BottomButtonOuter = styled.div`
  padding: 0 ${spacing(24)};
  margin: ${spacing(24)} 0;
`;
const DashboardButtonDesc = styled.div`
  font-size: ${spacing(12)};
`;
const ProgressContainer = styled.div`
  width: 100%;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaidLink = styled(Link)`
  display: block;
  margin-top: ${spacing(4)};
`;
